/**
 * usePublicShare
 *
 * Data hook for the public shared-map pages. Loads the presentation config,
 * handles the password gate, fetches the base ∩ viewer-filtered slim LIST and
 * MAP payloads in parallel, and registers a view. Per-artifact DETAIL is
 * fetched separately on open (see `usePublicArtifactDetail`). Store-free —
 * uses {@link publicShareService} only.
 *
 * Status model:
 * - 'loading'  — initial config fetch in flight
 * - 'password' — config loaded but a password is required and not yet verified
 * - 'ready'    — config + list + map data available
 * - 'notFound' — 404 (unknown token)
 * - 'revoked'  — 410 (revoked share)
 * - 'error'    — any other failure
 *
 * @module components/share/public/usePublicShare
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { SHARE_VIEWER_FILTER_DEBOUNCE_MS } from '@/constants';
import { isApiError } from '@/utils/apiErrorMessage';
import { useDebounce } from '@/hooks';
import { publicShareService } from '@/services/api';
import type {
  PublicShareConfig,
  PublicShareListData,
  PublicShareMapData,
  ShareViewerFilters,
} from '@/types/share';

const HTTP_NOT_FOUND = 404;
const HTTP_GONE = 410;
const HTTP_FORBIDDEN = 403;
const HTTP_TOO_MANY_REQUESTS = 429;

export type PublicShareStatus =
  | 'loading'
  | 'password'
  | 'ready'
  | 'notFound'
  | 'revoked'
  | 'error';

interface UsePublicShareResult {
  status: PublicShareStatus;
  config: PublicShareConfig | null;
  listData: PublicShareListData | null;
  mapData: PublicShareMapData | null;
  isDataLoading: boolean;
  viewerFilters: ShareViewerFilters;
  passwordError: boolean;
  rateLimited: boolean;
  setViewerFilters: (filters: ShareViewerFilters) => void;
  submitPassword: (password: string) => Promise<void>;
}

function httpStatusOf(error: unknown): number | undefined {
  return isApiError(error) ? error.status : undefined;
}

function statusFromError(error: unknown): PublicShareStatus {
  const status = httpStatusOf(error);
  if (status === HTTP_NOT_FOUND) return 'notFound';
  if (status === HTTP_GONE) return 'revoked';
  return 'error';
}

/**
 * Returns a copy of the viewer filters without the per-kind sort fields. The
 * MAP endpoint renders all matching geometry regardless of order, so a
 * sort-only change must not trigger a geometry refetch.
 */
function stripSortFields(filters: ShareViewerFilters): ShareViewerFilters {
  const {
    taskSortBy: _taskSortBy,
    taskSortDescending: _taskSortDescending,
    noteSortBy: _noteSortBy,
    noteSortDescending: _noteSortDescending,
    obstructionSortBy: _obstructionSortBy,
    obstructionSortDescending: _obstructionSortDescending,
    ...rest
  } = filters;
  return rest;
}

export function usePublicShare(token: string): UsePublicShareResult {
  const [status, setStatus] = useState<PublicShareStatus>('loading');
  const [config, setConfig] = useState<PublicShareConfig | null>(null);
  const [listData, setListData] = useState<PublicShareListData | null>(null);
  const [mapData, setMapData] = useState<PublicShareMapData | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [viewerFilters, setViewerFilters] = useState<ShareViewerFilters>({});
  const [passwordError, setPasswordError] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const viewRegistered = useRef(false);
  // Monotonic request counter — only the latest in-flight load may commit its
  // result (guards against out-of-order responses when filters change fast).
  const requestSeqRef = useRef(0);
  // Serialized filters of the last load we issued — lets the debounce effect
  // skip the initial fetch (already issued on config load) and no-op refetches.
  const lastLoadedKeyRef = useRef<string | null>(null);
  // Serialized sort-stripped filters of the last MAP load — lets a sort-only
  // change reuse the existing geometry instead of refetching the map payload.
  const lastMapKeyRef = useRef<string | null>(null);
  // Becomes true after the first successful load — distinguishes a recoverable
  // throttle on the live map from a 429 that blocked the very first load.
  const hasLoadedOnceRef = useRef(false);
  // Mirrors config.requiresPassword for use inside loadData without widening its deps.
  const requiresPasswordRef = useRef(false);

  const loadData = useCallback(
    async (filters: ShareViewerFilters) => {
      const requestSeq = (requestSeqRef.current += 1);
      lastLoadedKeyRef.current = JSON.stringify(filters);
      // The MAP payload ignores sort order, so only refetch geometry when a
      // non-sort filter changed (always on the first load — ref starts null).
      const mapFilters = stripSortFields(filters);
      const mapKey = JSON.stringify(mapFilters);
      const needMapFetch = lastMapKeyRef.current !== mapKey;
      setIsDataLoading(true);
      try {
        const [listResp, mapResp] = await Promise.all([
          publicShareService.getListData(token, filters),
          needMapFetch
            ? publicShareService.getMapData(token, mapFilters)
            : Promise.resolve(null),
        ]);
        if (requestSeq !== requestSeqRef.current) return;
        setListData(listResp.data);
        if (needMapFetch && mapResp) {
          setMapData(mapResp.data);
          lastMapKeyRef.current = mapKey;
        }
        setRateLimited(false);
        setStatus('ready');
        hasLoadedOnceRef.current = true;
        if (!viewRegistered.current) {
          viewRegistered.current = true;
          void publicShareService.registerView(token).catch(() => undefined);
        }
      } catch (error) {
        if (requestSeq !== requestSeqRef.current) return;
        const httpStatus = httpStatusOf(error);
        if (httpStatus === HTTP_FORBIDDEN) {
          // Password required (or cookie not present cross-site).
          setStatus('password');
          return;
        }
        if (httpStatus === HTTP_TOO_MANY_REQUESTS) {
          // Throttled: keep the last good data so the map stays usable. Only
          // surface a fatal screen when we never managed an initial load.
          setRateLimited(true);
          console.error('Public share data request rate-limited (429).');
          if (!hasLoadedOnceRef.current) {
            // A throttled cold load can't render the map. For a password-gated share
            // fall back to the gate (the viewer can still authenticate via the
            // separately rate-limited verify endpoint); otherwise the fatal screen.
            setStatus(requiresPasswordRef.current ? 'password' : 'error');
          }
          return;
        }
        setStatus(statusFromError(error));
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setIsDataLoading(false);
        }
      }
    },
    [token]
  );

  // Initial config load.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    publicShareService
      .getShare(token)
      .then((response) => {
        if (cancelled) return;
        setConfig(response.data);
        requiresPasswordRef.current = response.data.requiresPassword;
        // Always attempt a data load. When the share is password-protected and
        // the browser still holds a valid `share_pwd_<token>` cookie (set on a
        // prior verify, bound to the current password hash), the load succeeds
        // and we skip the gate. A 403 from `loadData` falls back to the
        // password gate — including after the owner changed the password
        // (cookie no longer matches the new hash).
        void loadData({});
      })
      .catch((error) => {
        if (cancelled) return;
        setStatus(statusFromError(error));
      });
    return () => {
      cancelled = true;
    };
  }, [token, loadData]);

  // The exposed `setViewerFilters` updates state immediately so the controlled
  // filter inputs stay responsive; the actual LIST + MAP refetch is debounced
  // here. Dedupes against the last-loaded key, so the initial fetch (issued by
  // the config-load effect) and round-trips back to the same filter state are
  // skipped.
  const { debouncedValue: debouncedViewerFilters } = useDebounce(
    viewerFilters,
    SHARE_VIEWER_FILTER_DEBOUNCE_MS
  );

  useEffect(() => {
    if (status !== 'ready') return;
    if (JSON.stringify(debouncedViewerFilters) === lastLoadedKeyRef.current) {
      return;
    }
    void loadData(debouncedViewerFilters);
  }, [debouncedViewerFilters, status, loadData]);

  const submitPassword = useCallback(
    async (password: string) => {
      setPasswordError(false);
      setRateLimited(false);
      try {
        await publicShareService.verifyPassword(token, password);
        await loadData(viewerFilters);
      } catch (error) {
        const httpStatus = httpStatusOf(error);
        if (httpStatus === HTTP_TOO_MANY_REQUESTS) {
          setRateLimited(true);
        } else {
          setPasswordError(true);
        }
      }
    },
    [token, loadData, viewerFilters]
  );

  return {
    status,
    config,
    listData,
    mapData,
    isDataLoading,
    viewerFilters,
    passwordError,
    rateLimited,
    setViewerFilters,
    submitPassword,
  };
}
